import { useEffect, useRef, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { putWithRetry } from '@/lib/blobUpload';
import type { components } from '@/api/generated/schema';

export type Vehicle = components['schemas']['VehicleResponse'];
export type VehicleAssignment = components['schemas']['VehicleAssignmentResponse'];
export type VehicleTool = components['schemas']['VehicleToolResponse'];
export type VehicleType = components['schemas']['VehicleType'];
export type VehicleStatus = components['schemas']['VehicleStatus'];
export type VehicleToolCondition = components['schemas']['VehicleToolCondition'];
export type SaveVehicleRequest = components['schemas']['SaveVehicleRequest'];
export type SaveVehicleToolRequest = components['schemas']['SaveVehicleToolRequest'];
export type SaveVehicleAssignmentRequest = components['schemas']['SaveVehicleAssignmentRequest'];

/**
 * The enum members the API accepts, as values the UI can iterate to build a
 * `<select>`.
 *
 * `satisfies` pins each entry to a real member of the generated union, so a
 * typo or a REMOVED member is a type error — the same trick, and the same
 * caveat, as M1-37's `LICENSE_CLASSES` and M1-38's `SKILL_PROFICIENCIES`: a
 * member ADDED on the API side still compiles green here and simply never
 * appears in the picker.
 */
export const VEHICLE_TYPES = [
  'Van',
  'Truck',
  'Trailer',
  'Other',
] as const satisfies readonly VehicleType[];

export const VEHICLE_STATUSES = [
  'Active',
  'InMaintenance',
  'Retired',
] as const satisfies readonly VehicleStatus[];

export const TOOL_CONDITIONS = [
  'Good',
  'Fair',
  'NeedsAttention',
  'OutOfService',
] as const satisfies readonly VehicleToolCondition[];

/** Thrown for the 403 a Technician gets when looking at someone ELSE's vehicle history. */
export class VehicleAssignmentsNotPermittedError extends Error {
  constructor() {
    super('Not permitted to view these vehicle assignments');
    this.name = 'VehicleAssignmentsNotPermittedError';
  }
}

/**
 * Thrown for the 400 the API returns when an assignment overlaps another.
 *
 * ⚠️ Carries the SERVER's own wording rather than a message invented here, the
 * same way M1-38's duplicate-label error does. There are two distinct cross-row
 * rules behind this one status — a person already in another vehicle, and a
 * vehicle that already has a primary driver — and they need different fixes.
 * Re-writing either into one generic "that clashes" would tell the office
 * something is wrong without telling them what to do about it.
 */
export class VehicleAssignmentClashError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'VehicleAssignmentClashError';
  }
}

const FLEET_KEY = ['vehicles'] as const;

function vehicleKey(vehicleId: string) {
  return ['vehicles', vehicleId] as const;
}

/**
 * Everything a fleet write can invalidate.
 *
 * ⚠️ <b>`['dispatch']` is in here, and it is not defensive.</b> The board's
 * composite read carries a SEPARATE, pre-resolved copy of the same answer — the
 * technician's vehicle and the maintenance warning both come from
 * `GET /api/v1/dispatch/technician-days`, not from these queries. Flipping a van
 * to in_maintenance without invalidating it would leave the board showing no
 * warning, with no error anywhere to suggest the edit had not taken. Exactly
 * the bug M1-39 records for its own mutations, and M1-38 before that for a
 * catalog rename.
 *
 * `['users']` is here for the same reason one step further out: a technician's
 * profile page renders their vehicle from their own assignment rows.
 */
function invalidateFleet(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: FLEET_KEY });
  void queryClient.invalidateQueries({ queryKey: ['dispatch'] });
  void queryClient.invalidateQueries({ queryKey: ['users'] });
}

/**
 * The account's vehicles (M1-27), retired ones included. `users.read`-gated, so
 * a Technician never calls it — the label and plate of the vehicle THEY drive
 * ride along on their own assignment rows instead (see
 * {@link useUserVehicleAssignments}), which is the only reason a technician's
 * own profile page can render without this.
 *
 * ⚠️ There is no `activeOnly` option and no `enabled` gate, and the API has no
 * such parameter either. An earlier draft had `activeOnly` for "the assignment
 * picker" — a screen that does not exist, since assignments are created from a
 * vehicle's own page — and an `enabled` argument no caller ever passed. Both
 * are gone for the same reason: an option with no caller is untested surface
 * held up by a doc comment. The story that introduces a picker, or a second
 * screen that must hold this query back, can add the one it actually needs.
 * See `VehicleEndpoints.List`.
 */
export function useVehicles() {
  return useQuery({
    queryKey: FLEET_KEY,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/vehicles');
      if (!response.ok || !data) {
        throw new Error(`GET /vehicles failed: ${response.status}`);
      }
      return data;
    },
    // ⚠️ `photoUrl`/`photoThumbnailUrl` are freshly minted read SAS links with
    // a 30-minute life (`AzureBlobPhotoStorageService.SasLifetime`). A fleet
    // page left open on a depot monitor never loses focus, so nothing would
    // otherwise refetch, and every thumbnail would quietly become a broken
    // image after half an hour. Same interval and the same reason as
    // `useJobPhotos` — which this flow borrowed its retry from and, until the
    // review of #113, not this.
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
    retry: false,
  });
}

export function useVehicle(vehicleId: string | undefined) {
  return useQuery({
    queryKey: vehicleKey(vehicleId ?? ''),
    enabled: Boolean(vehicleId),
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/vehicles/{vehicleId}', {
        params: { path: { vehicleId: vehicleId! } },
      });
      if (!response.ok || !data) {
        throw new Error(`GET /vehicles/{id} failed: ${response.status}`);
      }
      return data;
    },
    // ⚠️ `photoUrl`/`photoThumbnailUrl` are freshly minted read SAS links with
    // a 30-minute life (`AzureBlobPhotoStorageService.SasLifetime`). A fleet
    // page left open on a depot monitor never loses focus, so nothing would
    // otherwise refetch, and every thumbnail would quietly become a broken
    // image after half an hour. Same interval and the same reason as
    // `useJobPhotos` — which this flow borrowed its retry from and, until the
    // review of #113, not this.
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
    retry: false,
  });
}

export function useSaveVehicle(vehicleId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SaveVehicleRequest) => {
      const { data, response } =
        vehicleId === undefined
          ? await apiClient.POST('/api/v1/vehicles', { body })
          : await apiClient.PUT('/api/v1/vehicles/{vehicleId}', {
              params: { path: { vehicleId } },
              body,
            });
      if (!response.ok || !data) {
        throw new Error(`save vehicle failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => invalidateFleet(queryClient),
  });
}

// --- Tools ------------------------------------------------------------------

export function useVehicleTools(vehicleId: string | undefined) {
  return useQuery({
    queryKey: [...vehicleKey(vehicleId ?? ''), 'tools'] as const,
    enabled: Boolean(vehicleId),
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/vehicles/{vehicleId}/tools', {
        params: { path: { vehicleId: vehicleId! } },
      });
      if (!response.ok || !data) {
        throw new Error(`GET vehicle tools failed: ${response.status}`);
      }
      return data;
    },
    retry: false,
  });
}

/**
 * How long after the last keystroke before the API is asked.
 *
 * Module-private, not exported: nothing outside this file needs it, and an
 * exported symbol with no caller is the thing `useVehicles` just lost its
 * `enabled` parameter over. `useAddressSuggestions` exports its equivalent and
 * nothing imports that either. Export it when a caller exists.
 */
const TOOL_SEARCH_DEBOUNCE_MS = 250;

/**
 * "Which truck has the jetter?" — the AC's own question (M1-27).
 *
 * ⚠️ <b>An empty term is a real request, not a skipped one.</b> It returns the
 * whole fleet's equipment, because the screen's job is to show what the shop
 * owns and a search box that starts blank and shows nothing reads as broken.
 * That is a deliberate departure from the usual "don't fire until they type"
 * shape, and the API agrees rather than the UI papering over it.
 *
 * ⚠️ <b>Debounced HERE rather than in the component</b>, following
 * `useAddressSuggestions` — every caller pays for the same restraint, and this
 * one shipped without any: `search` went straight to the query key, so typing
 * "hydro jetter" issued TWELVE requests, each a leading-wildcard `ILIKE` with a
 * join. `useState(search)` seeds the debounced value, so the first render still
 * fires immediately and the empty-term load above is unaffected.
 *
 * ⚠️ <b>`placeholderData` keeps the previous answer on screen</b> while the
 * next one loads (`useJobs`' reason, on a far more frequent act). Without it
 * every keystroke was a fresh query key with no cached data, so `isPending` was
 * true and the results list was replaced by a loading state — the screen
 * blanking its own answer while somebody was still typing the question.
 */
export function useVehicleToolSearch(search: string) {
  const [debounced, setDebounced] = useState(search);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), TOOL_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  return useQuery({
    queryKey: ['vehicle-tools', debounced] as const,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/vehicle-tools', {
        params: { query: { search: debounced || undefined } },
      });
      if (!response.ok || !data) {
        throw new Error(`GET vehicle-tools failed: ${response.status}`);
      }
      return data;
    },
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function useSaveVehicleTool(vehicleId: string, toolId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SaveVehicleToolRequest) => {
      const { data, response } =
        toolId === undefined
          ? await apiClient.POST('/api/v1/vehicles/{vehicleId}/tools', {
              params: { path: { vehicleId } },
              body,
            })
          : await apiClient.PUT('/api/v1/vehicles/{vehicleId}/tools/{toolId}', {
              params: { path: { vehicleId, toolId } },
              body,
            });
      if (!response.ok || !data) {
        throw new Error(`save tool failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => {
      invalidateFleet(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['vehicle-tools'] });
    },
  });
}

export function useDeleteVehicleTool(vehicleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (toolId: string) => {
      const { response } = await apiClient.DELETE('/api/v1/vehicles/{vehicleId}/tools/{toolId}', {
        params: { path: { vehicleId, toolId } },
      });
      if (!response.ok) {
        throw new Error(`delete tool failed: ${response.status}`);
      }
    },
    onSuccess: () => {
      invalidateFleet(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['vehicle-tools'] });
    },
  });
}

// --- Assignments ------------------------------------------------------------

/** Who drives one vehicle, now and before — the vehicle detail page's read. */
export function useVehicleAssignments(vehicleId: string | undefined) {
  return useQuery({
    queryKey: [...vehicleKey(vehicleId ?? ''), 'assignments'] as const,
    enabled: Boolean(vehicleId),
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/vehicles/{vehicleId}/assignments', {
        params: { path: { vehicleId: vehicleId! } },
      });
      if (!response.ok || !data) {
        throw new Error(`GET vehicle assignments failed: ${response.status}`);
      }
      return data;
    },
    retry: false,
  });
}

/**
 * What one person drives, and what they used to — the profile page's read.
 *
 * ⚠️ Keyed under `['users', userId, …]` rather than under the fleet, because
 * that is the invalidation a profile-page edit expects and because it is the
 * question being asked. A 403 becomes {@link VehicleAssignmentsNotPermittedError}
 * so the page can say "you cannot see this" rather than "something went wrong"
 * — a Technician looking at somebody else's profile gets exactly that, the same
 * shape M1-38's `UserSkillsNotPermittedError` handles.
 */
export function useUserVehicleAssignments(userId: string | undefined) {
  return useQuery({
    queryKey: ['users', userId ?? '', 'vehicle-assignments'] as const,
    enabled: Boolean(userId),
    queryFn: async () => {
      const { data, response } = await apiClient.GET(
        '/api/v1/users/{userId}/vehicle-assignments',
        { params: { path: { userId: userId! } } },
      );
      if (response.status === 403) {
        throw new VehicleAssignmentsNotPermittedError();
      }
      if (!response.ok || !data) {
        throw new Error(`GET user vehicle assignments failed: ${response.status}`);
      }
      return data;
    },
    retry: false,
  });
}

/**
 * Reads the server's own wording out of a 400 on an assignment write.
 *
 * ⚠️ <b>The body comes from openapi-fetch's `error`, not from the `Response`.</b>
 * M1-38 records this one: the fetch has already consumed the body, so reading
 * it again yields nothing and the office sees a generic failure for a rule the
 * server explained perfectly well.
 */
function clashMessage(error: unknown): string | null {
  const problem = error as { errors?: Record<string, string[]> } | undefined;
  const first = problem?.errors
    ? Object.values(problem.errors).flat().find((message) => typeof message === 'string')
    : undefined;
  return first ?? null;
}

export function useSaveVehicleAssignment(vehicleId: string, assignmentId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SaveVehicleAssignmentRequest) => {
      const { data, error, response } =
        assignmentId === undefined
          ? await apiClient.POST('/api/v1/vehicles/{vehicleId}/assignments', {
              params: { path: { vehicleId } },
              body,
            })
          : await apiClient.PUT('/api/v1/vehicles/{vehicleId}/assignments/{assignmentId}', {
              params: { path: { vehicleId, assignmentId } },
              body,
            });
      if (response.status === 400) {
        const message = clashMessage(error);
        if (message) {
          throw new VehicleAssignmentClashError(message);
        }
      }
      if (!response.ok || !data) {
        throw new Error(`save assignment failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => invalidateFleet(queryClient),
  });
}

export function useDeleteVehicleAssignment(vehicleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (assignmentId: string) => {
      const { response } = await apiClient.DELETE(
        '/api/v1/vehicles/{vehicleId}/assignments/{assignmentId}',
        { params: { path: { vehicleId, assignmentId } } },
      );
      if (!response.ok) {
        throw new Error(`delete assignment failed: ${response.status}`);
      }
    },
    onSuccess: () => invalidateFleet(queryClient),
  });
}

// --- Photo (M1-44) ----------------------------------------------------------

/**
 * The content types the server's own allowlist accepts
 * (`VehicleEndpoints.AllowedPhotoContentTypes`). Checked here too so a wrong
 * file is refused before it is carried over the wire and rejected at the far
 * end — the same belt-and-braces split the job-photo flow uses.
 */
export const ALLOWED_VEHICLE_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Why an upload failed, in the terms the screen has to explain it.
 *
 * ⚠️ A union rather than a boolean, because M1-44's AC asks that a failure say
 * **what to do next**, and the four causes below have four different answers:
 * pick a different file, wait for someone to configure storage, check the
 * connection, or simply try again. "Something went wrong" is the one message
 * that is never actionable.
 */
export type VehiclePhotoFailure =
  | 'unsupported-type'
  | 'storage-unavailable'
  | 'network'
  | 'incomplete'
  | 'unknown';

/** `idle` covers "never started" and "finished" alike — neither needs saying. */
export type VehiclePhotoPhase = 'idle' | 'uploading' | 'processing' | 'failed';

/**
 * Drives one vehicle photo through M1-14's flow: request a SAS, PUT the file
 * straight to Blob Storage, confirm.
 *
 * ⚠️ **Only the PUT is retried** (`putWithRetry`), and a failure anywhere in
 * the flow keeps the chosen `File` so `retry()` replays that exact upload
 * rather than asking somebody to find the photo again. Re-picking a file is a
 * trivial cost on a desktop and a real one on a phone in a depot.
 *
 * ⚠️ The mutation is NOT a `useMutation`. It needs to hold a file across a
 * failure and expose a three-way phase — uploading, processing, failed — and
 * `isPending` collapses the first two, which are the ones worth telling apart:
 * the PUT is the long step, and `/complete` is where the server makes the
 * thumbnail.
 */
export function useUploadVehiclePhoto(
  vehicleId: string,
  /**
   * Called once the flow settles, so the screen can announce the outcome.
   * ⚠️ Announcing only the START — which is what this shipped with until the
   * review — leaves a screen-reader user told an upload began and never told
   * it finished: silence is then indistinguishable from a hang.
   */
  onOutcome?: (outcome: 'done' | 'failed') => void,
) {
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<VehiclePhotoPhase>('idle');
  const [failure, setFailure] = useState<VehiclePhotoFailure | null>(null);
  const [pending, setPending] = useState<File | null>(null);

  /**
   * ⚠️ A ref, not the `phase` state, because the guard has to hold WITHIN a
   * tick. Two clicks on Try again before React re-renders both read the same
   * stale `phase`, and state set in the first has not landed when the second
   * runs — so only a ref refuses the second. Without it both flows race and
   * whichever finishes first sets the panel idle while the other is still
   * uploading.
   */
  const inFlight = useRef(false);

  function fail(reason: VehiclePhotoFailure) {
    setPhase('failed');
    setFailure(reason);
    onOutcome?.('failed');
  }

  async function run(file: File) {
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    try {
      setPending(file);
      setFailure(null);

      if (!ALLOWED_VEHICLE_PHOTO_TYPES.includes(file.type)) {
        // ⚠️ Not retryable, so it does not keep the file: `retry()` would send
        // the same unusable image again. The screen asks for a different one.
        setPending(null);
        fail('unsupported-type');
        return;
      }

      setPhase('uploading');

      let uploadUrl: string;
      try {
        const requested = await apiClient.POST('/api/v1/vehicles/{vehicleId}/photo', {
          params: { path: { vehicleId } },
          body: { contentType: file.type },
        });
        if (!requested.response.ok || !requested.data) {
          // 503 is the server saying photo storage is not configured at all
          // (`NotConfigured`), which no amount of retrying fixes and which an
          // office manager can do nothing about — worth saying so rather than
          // offering a button that cannot work.
          fail(requested.response.status === 503 ? 'storage-unavailable' : 'unknown');
          return;
        }
        uploadUrl = requested.data.uploadUrl;
      } catch {
        // ⚠️ NOT `network`. This throw is a failed JSON round trip — an expired
        // token, a rejected fetch — and no PUT has been attempted, so the
        // "didn't get through after three tries" message would state something
        // that did not happen and send somebody to check a connection that is
        // fine. One blanket catch around the whole flow said exactly that
        // until the review of #113.
        fail('unknown');
        return;
      }

      try {
        await putWithRetry(uploadUrl, file);
      } catch {
        // Here `network` IS the truth: putWithRetry has spent all three
        // attempts against the blob endpoint before it throws.
        fail('network');
        return;
      }

      setPhase('processing');
      try {
        const completed = await apiClient.POST('/api/v1/vehicles/{vehicleId}/photo/complete', {
          params: { path: { vehicleId } },
        });
        if (!completed.response.ok || !completed.data) {
          // A 409 here is the server's "no file was found at the expected
          // location" — the upload genuinely did not land, and trying again is
          // exactly the right advice.
          fail(completed.response.status === 409 ? 'incomplete' : 'unknown');
          return;
        }

        // ⚠️ Seed the cache from the response rather than only invalidating.
        // `CompletePhotoUpload` returns the updated vehicle precisely so this
        // is possible, and without it the panel renders the PRE-upload status
        // until a refetch lands — "No photo yet" and an Add button, for the
        // width of a round trip, immediately after a successful upload. Same
        // reason `useUploadTechnicianPhoto` passes its response through.
        queryClient.setQueryData(vehicleKey(vehicleId), completed.data);
        setPending(null);
        setPhase('idle');
        onOutcome?.('done');
        invalidateFleet(queryClient);
      } catch {
        fail('unknown');
      }
    } finally {
      inFlight.current = false;
    }
  }

  return {
    phase,
    failure,
    /** The name of the file being uploaded, or the one that failed. */
    fileName: pending?.name ?? null,
    /** True only while this hook can replay something — see `unsupported-type`. */
    canRetry: pending !== null,
    upload: (file: File) => void run(file),
    retry: () => {
      if (pending) {
        void run(pending);
      }
    },
    dismiss: () => {
      setPhase('idle');
      setFailure(null);
      setPending(null);
    },
  };
}
